export interface PendingReceipt {
  number: string;
  amount: number;
  policyCompanyId: string;
  policyDepartment: string;
  effectDate: string;
  expirationDate: string;
  paymentPending: boolean;
  situationCode: string;
  blocked: boolean;
  admitsPayPalPayment: boolean;
  admitsRedsysPayment: boolean;
  admitsAmazonPayPayment: boolean;
  admitsBizumPayment: boolean;
  departmentType:
    | 'CAR'
    | 'HOME'
    | 'HOME_NSE'
    | 'HEALTH'
    | 'HEALTH_SERVICES'
    | 'DEATH'
    | 'PET'
    | 'BICIMAS'
    | 'LEX'
    | 'RENTAL'
    | 'ACCIDENT'
    | 'HUNTING'
    | 'BOAT'
    | 'COMMERCE'
    | 'GENERIC'
    | 'PAYMENT_PLAN_FEE';
  type: PendingReceiptProductType;
  risk?: string;
  policyNumber?: string;
  roDetailString?: string;
  policyName?: string;
  phoneNumbers?: string[];
  paymentPlanId?: string;
  paymentPlanEffectDate?: string;
  description?: string;
  departmentName?: string;
  redsysAgrup?: string;
  riskPostalCode?: string;
  carModel?: string;
  carPlate?: string;
  firstOfPolicy?: boolean;
  payerDocumentNumber?: string;
  payerFullName?: string;
  payerName?: string;
  payerSurname1?: string;
  payerSurname2?: string;
  remissionAppId?: string;
}

export interface ReceiptPolicy {
  policyNumber: string;
  situation: string;
  active: boolean;
}

export interface KoModalError {
  pageTitle: string;
  errorTitle: string;
  errorMessage: string;
  params: any;
}

export interface ReceiptPaymentPinValidationResult {
  token: string;
  businessId: string;
}

export interface ReceiptPaymentResult {
  remitted: boolean;
  remitTimeout: boolean;
  paymentRetained: boolean;
  documentsJwtToken: string;
}

export interface SavedIdentificationRetries {
  identificationRetriesCounter: number;
  timeStamp: number;
}

export interface PendingReceiptRODetail {
  receiptNumber?: string;
  documentNumber?: string;
  fullName?: string;
  carPlate?: string;
  amount?: number;
  effectDate?: string;
  expirationDate?: string;
  policyNumber?: string;
  paymentPlanId?: string;
  belongsToPaymentsPlan?: string;
  validationRetriesCounter?: number;
  pinCode?: string;
  phoneNumber?: string;
  risk?: string;
}

export enum ReceiptPaymentErrorType {
  SERVICE_GENERAL_ERROR = 'SERVICE_GENERAL_ERROR',
  RECEIPT_DETAIL_NOT_FOUND = 'RECEIPT_DETAIL_NOT_FOUND',
  PAYPAL_PAYMENT_NOT_COMPLETED = 'PAYPAL_PAYMENT_NOT_COMPLETED',
  PAYPAL_TOKEN_INCORRECT = 'PAYPAL_TOKEN_INCORRECT',
  PAYPAL_TIMEOUT = 'PAYPAL_TIMEOUT',
  REDSYS_OPERATION_DENIED = 'REDSYS_OPERATION_DENIED',
  REDSYS_OPERATION_CANCELLED = 'REDSYS_OPERATION_CANCELLED',
  REDSYS_TPV_ERROR = 'REDSYS_TPV_ERROR',
  RECEIPT_POLICY_NOT_ACTIVE = 'RECEIPT_POLICY_NOT_ACTIVE',
  RECEIPT_PAYMENT_NOT_PENDING = 'RECEIPT_PAYMENT_NOT_PENDING',
  RECEIPT_PAYMENT_NOT_ADMITTED = 'RECEIPT_PAYMENT_NOT_ADMITTED',
  RECEIPT_NEGATIVE_AMOUNT = 'RECEIPT_NEGATIVE_AMOUNT',
  RECEIPT_GESYCORE_BLOCKED = 'RECEIPT_GESYCORE_BLOCKED',
  RECEIPT_PAYMENT_CANCELED = 'RECEIPT_PAYMENT_CANCELED',
  REDSYS_PAYMENT_INVALID_RECEIPT_NUMBER = 'REDSYS_PAYMENT_INVALID_RECEIPT_NUMBER',
  PAYPAL_PAYMENT_INVALID_RECEIPT_NUMBER = 'PAYPAL_PAYMENT_INVALID_RECEIPT_NUMBER',
  RIC_VALIDATION_CLIENT_DUPLICATED = 'RIC_VALIDATION_CLIENT_DUPLICATED',
  RIC_VALIDATION_PHONE_INVALID = 'RIC_VALIDATION_PHONE_INVALID',
  RIC_VALIDATION_INVALID_POLICIES = 'RIC_VALIDATION_INVALID_POLICIES',
  RIC_VALIDATION_FIGURE_UNAUTHORIZED = 'RIC_VALIDATION_FIGURE_UNAUTHORIZED',
  RIC_CLIENT_NOT_FOUND = 'RIC_CLIENT_NOT_FOUND',

  // Locally thrown errors:
  LOCAL_REDSYS_TPV_URL_ERROR = 'LOCAL_REDSYS_TPV_URL_ERROR',
  LOCAL_REDSYS_SIGNATURE_VALIDATION_ERROR = 'LOCAL_REDSYS_SIGNATURE_VALIDATION_ERROR',
  LOCAL_REDSYS_CANCELED_MANUALLY = 'LOCAL_REDSYS_CANCELED_MANUALLY',
  LOCAL_PAYPAL_PREPARE_ERROR = 'LOCAL_PAYPAL_PREPARE_ERROR',
  LOCAL_PAYPAL_CANCELED_MANUALLY = 'LOCAL_PAYPAL_CANCELED_MANUALLY',
  LOCAL_AMAZON_PAY_CANCELED_MANUALLY = 'LOCAL_AMAZON_PAY_CANCELED_MANUALLY',
  LOCAL_RIC_ERROR = 'LOCAL_RIC_ERROR',
  LOCAL_RIC_TIMEOUT = 'LOCAL_RIC_TIMEOUT',
  LOCAL_RECEIPT_NOT_FIRST_OF_POLICY = 'LOCAL_RECEIPT_NOT_FIRST_OF_POLICY',
  PIN_INVALID = 'PIN_INVALID',
  PIN_MAX_ATTEMPTS_REACHED = 'PIN_MAX_ATTEMPTS_REACHED',
  NO_PENDING_RECEIPTS = 'NO_PENDING_RECEIPTS',
  LIFE_SERVICES_NOT_AVAILABLE = 'LIFE_SERVICES_NOT_AVAILABLE',
  DEFAULT = 'DEFAULT'
}

export enum ReceiptPaymentServiceError {
  SERVICE_GENERAL_ERROR = 'RECEIPTS_PAYMENT.SERVICE.GENERAL_ERROR',
  RECEIPT_DETAIL_NOT_FOUND = 'RECEIPTS_PAYMENT.RECEIPT.DETAIL.NOT_FOUND',
  PAYPAL_PAYMENT_NOT_COMPLETED = 'RECEIPTS_PAYMENT.PAYPAL.PAYMENT_NOT_COMPLETED',
  PAYPAL_TOKEN_INCORRECT = 'RECEIPTS_PAYMENT.PAYPAL.TOKEN_INCORRECT',
  PAYPAL_TIMEOUT = 'RECEIPTS_PAYMENT.PAYPAL.TIMEOUT',
  REDSYS_OPERATION_DENIED = 'RECEIPTS_PAYMENT.REDSYS.PAYMENT.OPERATION_DENIED',
  REDSYS_OPERATION_CANCELLED = 'RECEIPTS_PAYMENT.REDSYS.PAYMENT.OPERATION_CANCELLED',
  BIZUM_OPERATION_CANCELLED = 'RECEIPTS_PAYMENT.BIZUM.PAYMENT.OPERATION_CANCELLED',
  REDSYS_TPV_ERROR = 'RECEIPTS_PAYMENT.REDSYS.PAYMENT.TPV_ERROR',
  RECEIPT_POLICY_NOT_ACTIVE = 'RECEIPTS_PAYMENT.RECEIPT.VALIDATION.POLICY_NOT_ACTIVE',
  RECEIPT_PAYMENT_NOT_PENDING = 'RECEIPTS_PAYMENT.RECEIPT.VALIDATION.PAYMENT_NOT_PENDING',
  RECEIPT_PAYMENT_NOT_ADMITTED = 'RECEIPTS_PAYMENT.RECEIPT.VALIDATION.PAYMENT_NOT_ADMITTED',
  RECEIPT_NEGATIVE_AMOUNT = 'RECEIPTS_PAYMENT.RECEIPT.VALIDATION.NEGATIVE_AMOUNT',
  RECEIPT_GESYCORE_BLOCKED = 'RECEIPTS_PAYMENT.RECEIPT.VALIDATION.GESYCORE_BLOCKED',
  RECEIPT_PAYMENT_CANCELED = 'RECEIPTS_PAYMENT.RECEIPT.VALIDATION.USER_CANCELED',
  REDSYS_PAYMENT_INVALID_RECEIPT_NUMBER = 'RECEIPTS_PAYMENT.REDSYS.PAYMENT.INVALID_RECEIPT_NUMBER',
  PAYPAL_PAYMENT_INVALID_RECEIPT_NUMBER = 'RECEIPTS_PAYMENT.PAYPAL.PAYMENT.INVALID_RECEIPT_NUMBER',
  RIC_VALIDATION_CLIENT_DUPLICATED = 'RECEIPTS_PAYMENT.RIC.VALIDATION.CLIENT_DUPLICATED',
  RIC_VALIDATION_PHONE_INVALID = 'RECEIPTS_PAYMENT.RIC.VALIDATION.PHONE_INVALID',
  RIC_VALIDATION_INVALID_POLICIES = 'RECEIPTS_PAYMENT.RIC.VALIDATION.INVALID_POLICIES',
  RIC_VALIDATION_FIGURE_UNAUTHORIZED = 'RECEIPTS_PAYMENT.RIC.VALIDATION.FIGURE_UNAUTHORIZED',
  RIC_CLIENT_NOT_FOUND = 'RECEIPTS_PAYMENT.RIC.CLIENT.NOT_FOUND',

  // Locally thrown errors:
  LOCAL_REDSYS_TPV_URL_ERROR = 'LOCAL_REDSYS_TPV_URL_ERROR',
  LOCAL_REDSYS_SIGNATURE_VALIDATION_ERROR = 'LOCAL_REDSYS_SIGNATURE_VALIDATION_ERROR',
  LOCAL_REDSYS_CANCELED_MANUALLY = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.REDSYS.REDSYS_GENERIC_ERROR',
  LOCAL_PAYPAL_PREPARE_ERROR = 'LOCAL_PAYPAL_PREPARE_ERROR',
  LOCAL_RIC_ERROR = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.VALIDATION_RIC_ERROR',
  LOCAL_RIC_TIMEOUT = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.VALIDATION_RIC_ERROR',
  LOCAL_PAYPAL_CANCELED_MANUALLY = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.PAYPAL.PAYPAL_GENERIC_ERROR',
  LOCAL_AMAZON_PAY_CANCELED_MANUALLY = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.AMAZON_PAY.AMAZON_PAY_GENERIC_',
  LOCAL_RECEIPT_NOT_FIRST_OF_POLICY = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.RECEIPT_NOT_FIRST_OF_POLICY',
  PIN_INVALID = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.PIN_INVALID',
  PIN_MAX_ATTEMPTS_REACHED = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.PIN_MAX_ATTEMPTS_REACHED',
  NO_PENDING_RECEIPTS = 'RECEIPT_PAYMENT.ERROR.CODE_VALIDATION.NO_PENDING_RECEIPTS',
  LIFE_SERVICES_NOT_AVAILABLE = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.LIFE_SERVICES_NOT_AVAILABLE',
  DEFAULT = 'RECEIPT_PAYMENT.ERROR.GENERAL'
}

export enum ReceiptPaymentErrorTranslationKey {
  SERVICE_GENERAL_ERROR = 'RECEIPT_PAYMENT.ERROR.GENERAL',
  RECEIPT_DETAIL_NOT_FOUND = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.RECEIPT_NOT_FOUND',
  PAYPAL_PAYMENT_NOT_COMPLETED = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.PAYPAL.PAYPAL_GENERIC_ERROR',
  PAYPAL_TOKEN_INCORRECT = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.PAYPAL.PAYPAL_GENERIC_ERROR',
  PAYPAL_TIMEOUT = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.PAYPAL.PAYPAL_GENERIC_ERROR',
  REDSYS_OPERATION_DENIED = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.REDSYS.REDSYS_GENERIC_ERROR',
  REDSYS_OPERATION_CANCELLED = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.REDSYS.REDSYS_GENERIC_ERROR',
  BIZUM_OPERATION_CANCELLED = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.REDSYS.REDSYS_GENERIC_ERROR',
  REDSYS_TPV_ERROR = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.REDSYS.REDSYS_GENERIC_ERROR',
  RECEIPT_POLICY_NOT_ACTIVE = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.POLICY_NOT_ACTIVE',
  RECEIPT_PAYMENT_NOT_PENDING = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.PAYMENT_NOT_PENDING',
  RECEIPT_PAYMENT_NOT_ADMITTED = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.PAYMENT_NOT_ADMITTED',
  RECEIPT_NEGATIVE_AMOUNT = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.NEGATIVE_AMOUNT',
  RECEIPT_GESYCORE_BLOCKED = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.GESYCORE_BLOCKED',
  REDSYS_PAYMENT_INVALID_RECEIPT_NUMBER = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.REDSYS.REDSYS_GENERIC_ERROR',
  PAYPAL_PAYMENT_INVALID_RECEIPT_NUMBER = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.PAYPAL.INVALID_RECEIPT_NUMBER',
  RIC_VALIDATION_CLIENT_DUPLICATED = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.CLIENT_NOT_FOUND',
  RIC_VALIDATION_PHONE_INVALID = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.VALIDATION_PHONE_INVALID',
  RIC_VALIDATION_INVALID_POLICIES = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.VALIDATION_INVALID_POLICIES',
  RIC_VALIDATION_FIGURE_UNAUTHORIZED = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.VALIDATION_INVALID_POLICIES',
  RIC_CLIENT_NOT_FOUND = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.CLIENT_NOT_FOUND',
  RECEIPT_PAYMENT_CANCELED = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.CANCELED',

  // Locally thrown errors:
  LOCAL_REDSYS_TPV_URL_ERROR = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.REDSYS.REDSYS_GENERIC_ERROR',
  LOCAL_REDSYS_SIGNATURE_VALIDATION_ERROR = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.REDSYS.REDSYS_GENERIC_ERROR',
  LOCAL_REDSYS_CANCELED_MANUALLY = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.REDSYS.REDSYS_GENERIC_ERROR',
  LOCAL_PAYPAL_PREPARE_ERROR = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.PAYPAL.PAYPAL_GENERIC_ERROR',
  LOCAL_PAYPAL_CANCELED_MANUALLY = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.PAYPAL.PAYPAL_GENERIC_ERROR',
  LOCAL_AMAZON_PAY_CANCELED_MANUALLY = 'RECEIPT_PAYMENT.ERROR.RECEIPT_PAYMENT.AMAZON_PAY.AMAZON_PAY_GENERIC_ERROR',
  LOCAL_RIC_ERROR = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.VALIDATION_RIC_ERROR',
  LOCAL_RIC_TIMEOUT = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.VALIDATION_RIC_ERROR',
  LOCAL_RECEIPT_NOT_FIRST_OF_POLICY = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.RECEIPT_NOT_FIRST_OF_POLICY',
  PIN_INVALID = 'RECEIPT_PAYMENT.ERROR.CODE_VALIDATION.RETRIES_REMAINING',
  PIN_MAX_ATTEMPTS_REACHED = 'RECEIPT_PAYMENT.ERROR.CODE_VALIDATION.NO_RETRIES_REMAINING',
  NO_PENDING_RECEIPTS = 'RECEIPT_PAYMENT.ERROR.CODE_VALIDATION.NO_PENDING_RECEIPTS',
  LIFE_SERVICES_NOT_AVAILABLE = 'RECEIPT_PAYMENT.ERROR.RECEIPT_IDENTIFICATION.LIFE_SERVICES_NOT_AVAILABLE',
  DEFAULT = 'RECEIPT_PAYMENT.ERROR.GENERAL'
}

export enum DocumentType {
  NIF = 'NIF',
  NIE = 'NIE',
  PASSPORT = 'Pasaporte',
  OTHER_COUNTRIES = 'DNI Otros países'
}

export enum ReceiptPaymentOrigin {
  SMS = 'SMS',
  CORREO = 'CORREO',
  APP = 'APP'
}

export enum PendingReceiptProductType {
  GENERAL = 'GENERAL',
  LIFE = 'LIFE',
  PAYMENT_PLAN_FEE = 'PAYMENT_PLAN_FEE'
}

export type TpvPaymentType = 'bizum' | 'redsys';

